import { useCallback, useContext, useEffect } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useStore } from 'react-redux'
import { useAppSelector } from 'hooks/reduxHooks'
import { getAccommodationPropertyTimeZone } from 'lib/datetime/accommodationPropertyTimeZone'
import { getAllOffers } from 'selectors/offerSelectors'
import { createActorContext } from '@xstate/react'
import multiCartMachine, { CART_MODAL_EVENT_NAME } from './multiCartMachine'
import { setCartMachineActor } from './getCartMachineActor'
import type { MachineContext } from 'typings/app/machineContext'
import ModalContext from 'contexts/ModalContext'
import SharedCartsCartSelectionModal from 'components/Pages/UserCartPage/SharedCarts/Modals/SharedCartsCartSelectionModal'
import { useAppNavigation } from 'lib/routing/useAppNavigation'
import { useLocaleUrlOptions } from 'hooks/useLocaleUrlOptions'

// Cart machine is not needed at server side, initialise with server side state at client side, similar to redux.

export const MultiCartContext = createActorContext(multiCartMachine)

interface ReduxConnectedCartProviderProps {
  children: React.ReactNode
}

// Component to capture the actor reference
function CartMachineActorRegistry({ children }: { children: React.ReactNode }) {
  const actor = MultiCartContext.useActorRef()

  useEffect(() => {
    setCartMachineActor(actor)
    return () => {
      setCartMachineActor(null)
    }
  }, [actor])

  // Development-only state change logging
  useEffect(() => {
    if (actor) {
      const subscription = actor.subscribe((snapshot) => {
        console.debug('🛒 MultiCart Machine State Change')
        console.debug('State:', snapshot.value)
        console.debug('Context:', snapshot.context)
      })

      return () => {
        subscription.unsubscribe()
      }
    }
  }, [actor])

  return <>{children}</>
}

export function CartMachineModalListener({ children }: { children: React.ReactNode }) {
  const showModal = useContext(ModalContext)

  useEffect(() => {
    const onCartShowModalEvent = (e: CustomEvent<MachineContext.CartModalConfig>) => {
      // If we introduce more modal types, we can add a switch statement here to show the appropriate modal
      showModal(<SharedCartsCartSelectionModal multiCartItemIdsToMove={e.detail.multiCartItemIdsToMove} />)
    }

    document.addEventListener(CART_MODAL_EVENT_NAME, onCartShowModalEvent as any)

    return () => {
      document.removeEventListener(CART_MODAL_EVENT_NAME, onCartShowModalEvent as any)
    }
  }, [showModal])

  return <>{children}</>
}

function getLoadedOfferTimeZoneKey(state: App.State): string {
  return Object.entries(getAllOffers(state))
    .flatMap(([offerId, offer]) => {
      if (!offer || !('property' in offer)) return []
      const timeZone = getAccommodationPropertyTimeZone(offer as { property?: { timezone?: string } })
      return timeZone ? [`${offerId}:${timeZone}`] : []
    })
    .sort()
    .join('|')
}

function CartExpiredSweepOnOffersLoaded() {
  const actor = MultiCartContext.useActorRef()
  const store = useStore<App.State>()

  useEffect(() => {
    let previousTimeZoneKey = getLoadedOfferTimeZoneKey(store.getState())

    return store.subscribe(() => {
      const nextTimeZoneKey = getLoadedOfferTimeZoneKey(store.getState())
      if (nextTimeZoneKey === previousTimeZoneKey) return
      previousTimeZoneKey = nextTimeZoneKey
      if (actor.getSnapshot().value !== 'ready') return
      actor.send({ type: 'retry expired cart auto delete' })
    })
  }, [actor, store])

  return null
}

export function MultiCartMachineProvider({ children }: ReduxConnectedCartProviderProps) {
  const intl = useIntl()
  const store = useStore<App.State>()
  const memberId = useAppSelector((state: App.State) => state.auth.account.memberId)
  const navigation = useAppNavigation()
  const currentRegionCode = useAppSelector((state: App.State) => state.geo.currentRegionCode)
  const headlessMode = useAppSelector((state: App.State) => state.system.headlessMode)
  const { languageOptions } = useLocaleUrlOptions()
  const getOfferById = useCallback((offerId: string) => {
    const offer = getAllOffers(store.getState())[offerId]
    if (!offer || !('property' in offer)) return undefined
    return { property: (offer as { property?: { timezone?: string } }).property }
  }, [store])

  return (
    <MultiCartContext.Provider options={{ input: { memberId, navigation, currentRegionCode, languageOptions, headlessMode, intl, getOfferById } }}>
      <CartMachineActorRegistry>
        <CartExpiredSweepOnOffersLoaded />
        <CartMachineModalListener>
          {children}
        </CartMachineModalListener>
      </CartMachineActorRegistry>
    </MultiCartContext.Provider>
  )
}
